import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AddGoalRaceModal from './AddGoalRaceModal';
import LogResultModal from './LogResultModal';
import ConfirmDialog from '../ConfirmDialog';
import './RacesPage.css';

const RacesPage = () => {
  const { authFetch } = useAuth();
  const [goalRaces, setGoalRaces] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('goals');
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showLogResultModal, setShowLogResultModal] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, id: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [goalsRes, resultsRes] = await Promise.all([
        authFetch('/api/races/goals'),
        authFetch('/api/races/results')
      ]);

      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setGoalRaces(data.goal_races || []);
      }

      if (resultsRes.ok) {
        const data = await resultsRes.json();
        setResults(data.results || []);
      }
    } catch (err) {
      console.error('Failed to load races data:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditGoal = (race) => {
    setEditingRace(race);
  };

  const handleDeleteGoal = async (raceId) => {
    try {
      const res = await authFetch(`/api/races/goals/${raceId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        loadData();
      }
    } catch (err) {
      console.error('Failed to delete goal race:', err);
    }
  };

  const handleDeleteResult = async (resultId) => {
    try {
      const res = await authFetch(`/api/races/results/${resultId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        loadData();
      }
    } catch (err) {
      console.error('Failed to delete race result:', err);
    }
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ isOpen: false, type: null, id: null });

    if (type === 'goal') {
      await handleDeleteGoal(id);
    } else if (type === 'result') {
      await handleDeleteResult(id);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDistance = (distance) => {
    const names = {
      '5k': '5K',
      '10k': '10K',
      'half_marathon': 'Half Marathon',
      'marathon': 'Marathon',
      'ultra': 'Ultra'
    };
    return names[distance] || distance;
  };

  const getDaysUntil = (dateStr) => {
    const race = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    race.setHours(0, 0, 0, 0);
    const diff = Math.ceil((race - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="races-page">
        <div className="races-loading">Loading races...</div>
      </div>
    );
  }

  return (
    <div className="races-page">
      <div className="races-container">
        <div className="races-header">
          <div className="races-header__left">
            <h1>Races</h1>
            <p>Track your goals and log your results</p>
          </div>
          <div className="races-header__actions">
            {activeTab === 'goals' ? (
              <button
                className="btn-primary"
                onClick={() => setShowAddGoalModal(true)}
              >
                Add Goal Race
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={() => setShowLogResultModal(true)}
              >
                Log Result
              </button>
            )}
          </div>
        </div>

        <div className="races-tabs">
          <button
            className={`tab ${activeTab === 'goals' ? 'active' : ''}`}
            onClick={() => setActiveTab('goals')}
          >
            Goal Races
          </button>
          <button
            className={`tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            Results
          </button>
        </div>

        {activeTab === 'goals' ? (
          <div className="races-content">
            {goalRaces.length === 0 ? (
              <div className="races-empty">
                <div className="races-empty__icon">🏁</div>
                <h3>No Goal Races</h3>
                <p>Add a race you're training for</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowAddGoalModal(true)}
                >
                  Add Goal Race
                </button>
              </div>
            ) : (
              <div className="goal-races-list">
                {goalRaces.map(race => {
                  const daysUntil = getDaysUntil(race.race_date);
                  const isPast = daysUntil < 0;

                  return (
                    <div
                      key={race.id}
                      className={`goal-race-card ${race.is_primary ? 'primary' : ''} ${isPast ? 'past' : ''}`}
                    >
                      {!!race.is_primary && (
                        <span className="primary-badge">Primary Goal</span>
                      )}
                      <div className="goal-race-card__header">
                        <h3>{race.custom_name || race.library_race_name || 'Goal Race'}</h3>
                        <span className="distance-badge">{formatDistance(race.distance)}</span>
                      </div>
                      <div className="goal-race-card__date">
                        {formatDate(race.race_date)}
                        {!isPast && (
                          <span className="days-until">
                            {daysUntil === 0 ? 'Today!' : `${daysUntil} days`}
                          </span>
                        )}
                      </div>
                      {race.target_time && (
                        <div className="goal-race-card__target">
                          Target: {race.target_time}
                        </div>
                      )}
                      {race.notes && (
                        <div className="goal-race-card__notes">{race.notes}</div>
                      )}
                      <div className="goal-race-card__footer">
                        <button
                          className="btn-edit"
                          onClick={() => handleEditGoal(race)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => setDeleteConfirm({ isOpen: true, type: 'goal', id: race.id })}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="races-content">
            {results.length === 0 ? (
              <div className="races-empty">
                <div className="races-empty__icon">🏅</div>
                <h3>No Race Results</h3>
                <p>Log your race performances</p>
                <button
                  className="btn-primary"
                  onClick={() => setShowLogResultModal(true)}
                >
                  Log Result
                </button>
              </div>
            ) : (
              <div className="results-list">
                {results.map(result => (
                  <div key={result.id} className="result-card">
                    <div className="result-card__header">
                      <h3>{result.race_name}</h3>
                      <span className="distance-badge">{formatDistance(result.distance)}</span>
                    </div>
                    <div className="result-card__date">{formatDate(result.race_date)}</div>
                    <div className="result-card__time">
                      <span className="time-value">{result.finish_time}</span>
                      {result.position && (
                        <span className="position">#{result.position}</span>
                      )}
                    </div>
                    {result.notes && (
                      <div className="result-card__notes">{result.notes}</div>
                    )}
                    <div className="result-card__footer">
                      <button
                        className="btn-delete"
                        onClick={() => setDeleteConfirm({ isOpen: true, type: 'result', id: result.id })}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {(showAddGoalModal || editingRace) && (
        <AddGoalRaceModal
          onClose={() => {
            setShowAddGoalModal(false);
            setEditingRace(null);
          }}
          onCreated={() => {
            setShowAddGoalModal(false);
            setEditingRace(null);
            loadData();
          }}
          race={editingRace}
        />
      )}

      {showLogResultModal && (
        <LogResultModal
          onClose={() => setShowLogResultModal(false)}
          onCreated={() => {
            setShowLogResultModal(false);
            loadData();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={deleteConfirm.type === 'goal' ? 'Delete Goal Race' : 'Delete Race Result'}
        message={deleteConfirm.type === 'goal'
          ? 'Are you sure you want to delete this goal race?'
          : 'Are you sure you want to delete this race result?'}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, type: null, id: null })}
      />
    </div>
  );
};

export default RacesPage;
